import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { importsApi, type ImportItem, type ImportVersion } from '@/lib/imports';
import { aed, dayMonth } from '@/lib/format';
import ItemDrawer from './ItemDrawer';

interface Props {
  version: ImportVersion;
  onVersion: (v: ImportVersion) => void;
  onNext: () => void;
  onBack: () => void;
}

type ConfidenceFilter = 'all' | 'high' | 'check' | 'low';

const confidenceStyle: Record<string, string> = {
  high: 'bg-primary-50 text-primary-800 border-primary-200',
  check: 'bg-secondary-50 text-secondary-800 border-secondary-200',
  low: 'bg-accent-100 text-accent-900 border-accent-300',
};

/** Проверка строк на реальных данных: правка цены/остатка/группы, флаги проверок (п.11.3), риск в AED (п.11.4),
 * флажок «Проверено» (обязателен для строк с риском/флагами/низкой уверенностью), исключение строки из публикации. */
export default function StepReview({ version, onVersion, onNext, onBack }: Props) {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [items, setItems] = useState<ImportItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ConfidenceFilter>('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [marked, setMarked] = useState<number | null>(null);

  const confidenceLabel: Record<string, string> = { high: t('impReview.high'), check: t('impReview.check'), low: t('impReview.low') };

  const load = useCallback(async () => {
    try {
      const [list, v] = await Promise.all([importsApi.items(version.id), importsApi.get(version.id)]);
      setItems(list.filter((i) => !i.duplicateOf));
      onVersion(v);
      setError(null);
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, [version.id, onVersion]);

  useEffect(() => {
    void load();
  }, [load]);

  const all = useMemo(() => items ?? [], [items]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((item) => {
      const matchesConfidence = filter === 'all' || item.confidence === filter;
      const matchesQuery = q === '' || item.supplierArticle.toLowerCase().includes(q) || (item.name ?? '').toLowerCase().includes(q) || (item.color ?? '').toLowerCase().includes(q);
      const matchesPending = !showPendingOnly || (item.reviewRequired && !item.reviewed);
      return matchesConfidence && matchesQuery && matchesPending;
    });
  }, [all, filter, query, showPendingOnly]);

  const pending = all.filter((i) => i.reviewRequired && !i.reviewed && !i.excluded).length;
  const included = all.filter((i) => !i.excluded).length;
  const duplicates = version.pagesLog.reduce((s, p) => s + p.items, 0) - all.length;
  const sharedGroups = new Set(all.map((i) => i.stockGroup).filter(Boolean)).size;

  const patch = async (item: ImportItem, body: Record<string, unknown>) => {
    setEditing(null);
    setBusy(true);
    try {
      const updated = await importsApi.patchItem(version.id, item.id, body);
      setItems((prev) => prev?.map((i) => (i.id === item.id ? updated : i)) ?? null);
      setError(null);
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  const reviewAll = async (force = false) => {
    setBusy(true);
    try {
      const r = await importsApi.reviewAll(version.id, force);
      setMarked(r.marked);
      await load();
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  const attachImages = async () => {
    setBusy(true);
    try {
      await importsApi.attachImages(version.id);
      await load();
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  const count = (key: ConfidenceFilter) => (key === 'all' ? all.length : all.filter((i) => i.confidence === key).length);
  const flagLabel = (f: string) => t(`impX.flag.${f}`, { defaultValue: f });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2.5">
          <i className="ri-links-line mt-0.5 text-[15px] text-accent-700" />
          <p className="text-[12px] leading-5 text-accent-900">
            <span className="font-semibold">{t('impReview.banner1', { count: Math.max(0, duplicates) })}</span>,{' '}
            <span className="font-semibold">{t('impReview.banner2', { count: sharedGroups })}</span>.{' '}
            {pending > 0 ? t('impX.pendingBanner', { count: pending, risk: Math.round(version.totalRiskAed).toLocaleString('en-AE') }) : t('impX.allReviewed')}
            {marked != null && <span className="ml-1 font-semibold">{t('impX.reviewMarked', { count: marked, pending })}</span>}
            <span className="mt-0.5 block text-[11px] font-normal text-accent-800/80">{t('impX.reviewRuleNote')}</span>
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setShowPendingOnly((v) => !v)}
            className="inline-flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-2.5 py-1 text-[11px] font-semibold text-accent-900 hover:bg-accent-100"
          >
            <i className="ri-filter-3-line text-[11px]" /> {showPendingOnly ? t('impReview.showAll') : t('impX.showPending')}
          </button>
          {version.fileName?.toLowerCase().endsWith('.pdf') && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void attachImages()}
              className="inline-flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-2.5 py-1 text-[11px] font-semibold text-accent-900 hover:bg-accent-100 disabled:opacity-50"
              title={t('imd.attachHint')}
            >
              <i className="ri-image-add-line text-[11px]" /> {t('imd.attach', { count: all.filter((i) => i.imageUrl).length, total: all.length })}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => void reviewAll()}
            className="inline-flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-2.5 py-1 text-[11px] font-semibold text-accent-900 hover:bg-accent-100 disabled:opacity-50"
            title={t('impX.reviewAllHint')}
          >
            <i className="ri-check-double-line text-[11px]" /> {t('impX.reviewAll')}
          </button>
          {me?.role === 'admin' && pending > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void reviewAll(true)}
              className="inline-flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-rose-300 bg-background-50 px-2.5 py-1 text-[11px] font-semibold text-rose-800 hover:bg-rose-50 disabled:opacity-50"
              title={t('impX.reviewForceHint')}
            >
              <i className="ri-shield-check-line text-[11px]" /> {t('impX.reviewForce', { count: pending })}
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
          {t('ui.error')}: {error}{' '}
          <button type="button" onClick={() => void load()} className="cursor-pointer font-semibold underline">
            {t('ui.retry')}
          </button>
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground-950">
              {t('impReview.title')}
              <span className="tnum ml-2 rounded bg-background-100 px-1.5 py-0.5 text-[10.5px] font-medium text-foreground-600">{t('impReview.draft', { version: version.version })}</span>
            </h2>
            <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('impReview.sub')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
            {(['all', 'high', 'check', 'low'] as ConfidenceFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`cursor-pointer whitespace-nowrap rounded px-2 py-1 text-[11px] font-medium transition-colors ${filter === f ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'}`}
              >
                {f === 'all' ? t('impReview.all') : confidenceLabel[f]} <span className="tnum text-foreground-400">{count(f)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 border-b border-background-200 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-[280px]">
            <i className="ri-search-line pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-foreground-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('impReview.searchPlaceholder')} aria-label={t('impReview.searchAria')} className="h-7 w-full rounded border border-background-200 bg-background-100 pl-7 pr-2 text-[11.5px] outline-none focus:border-primary-400 focus:bg-background-50" />
          </div>
          <p className="text-[11px] text-foreground-500">
            {t('impReview.rowsShown', { shown: rows.length, total: all.length })}
            {' · '}
            <span className="text-foreground-700">{t('impReview.selected', { count: included })}</span>
          </p>
        </div>

        <div className="max-h-[560px] overflow-auto thin-scroll">
          <table className="w-full min-w-[1520px] border-collapse text-[12px]">
            <thead className="sticky top-0 z-10 bg-background-100 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
              <tr className="border-b border-background-200">
                <th className="w-9 px-3 py-2" title={t('impX.includeHint')}>
                  <i className="ri-checkbox-line text-[12px]" />
                </th>
                <th className="px-2 py-2 font-medium">{t('impReview.colItem')}</th>
                <th className="px-2 py-2 font-medium">{t('impReview.colCategory')}</th>
                <th className="px-2 py-2 font-medium">{t('impReview.colColour')}</th>
                <th className="px-2 py-2 text-right font-medium">{t('impReview.colStockNow')}</th>
                <th className="px-2 py-2 font-medium">{t('impReview.colIncoming')}</th>
                <th className="px-2 py-2 text-right font-medium">{t('impReview.colPartnerPrice')}</th>
                <th className="px-2 py-2 text-right font-medium">{t('impReview.colOldPrice')}</th>
                <th className="px-2 py-2 font-medium">{t('impReview.colOptions')}</th>
                <th className="px-2 py-2 font-medium">{t('impReview.colStockGroup')}</th>
                <th className="px-2 py-2 text-right font-medium">{t('impX.colRisk')}</th>
                <th className="px-2 py-2 font-medium">{t('impX.colFlags')}</th>
                <th className="px-2 py-2 font-medium">{t('impReview.colConfidence')}</th>
                <th className="px-2 py-2 font-medium">{t('impX.colReviewed')}</th>
              </tr>
            </thead>
            <tbody>
              {items === null && (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-center text-foreground-400">
                    {t('ui.loading')}
                  </td>
                </tr>
              )}
              {rows.map((item) => {
                const low = item.confidence === 'low';
                const needs = item.reviewRequired && !item.reviewed;
                return (
                  <tr key={item.id} className={`border-b border-background-200/70 transition-colors last:border-0 ${item.excluded ? 'opacity-50' : ''} ${low || needs ? 'bg-accent-50/70 hover:bg-accent-50' : 'hover:bg-background-100/60'}`}>
                    <td className="px-3 py-2 align-middle">
                      <input type="checkbox" aria-label={t('impReview.selectRow', { sku: item.supplierArticle })} checked={!item.excluded} onChange={() => void patch(item, { excluded: !item.excluded })} className="h-3.5 w-3.5 cursor-pointer accent-[oklch(var(--primary-500))]" />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex min-w-0 max-w-[300px] cursor-pointer items-center gap-2" onClick={() => setOpenId(item.id)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenId(item.id)}>
                        {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded border border-background-200 bg-white object-contain" /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-dashed border-background-300 text-[10px] text-foreground-300"><i className="ri-image-line" /></span>}
                        <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="tnum text-[10.5px] font-medium text-foreground-500">{item.supplierArticle}</p>
                          {item.page && <span className="text-[9.5px] text-foreground-400">p.{item.page}</span>}
                          {item.edited && <span className="rounded bg-secondary-100 px-1 text-[9.5px] font-medium text-secondary-800">{t('impX.edited')}</span>}
                          {item.specialOffer && <span className="rounded bg-accent-100 px-1 text-[9.5px] font-medium text-accent-800">{t('impX.special')}</span>}
                          {item.discontinued && <span className="rounded bg-rose-100 px-1 text-[9.5px] font-medium text-rose-700">{t('impX.discontinued')}</span>}
                        </div>
                        <p className="truncate text-[12px] font-medium text-foreground-900 hover:text-primary-700">{item.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-foreground-600">{item.category}</td>
                    <td className="px-2 py-2 text-foreground-600">{item.color}</td>
                    <td className="px-2 py-2 text-right">
                      {editing === `${item.id}-stock` ? (
                        <input autoFocus type="number" defaultValue={item.stock ?? 0} onBlur={(e) => void patch(item, { stock: Number(e.target.value) })} onKeyDown={(e) => { if (e.key === 'Enter') void patch(item, { stock: Number((e.target as HTMLInputElement).value) }); }} className="tnum h-6 w-16 rounded border border-primary-400 bg-background-50 px-1 text-right text-[11.5px] outline-none" />
                      ) : (
                        <button type="button" onClick={() => setEditing(`${item.id}-stock`)} className={`tnum cursor-pointer rounded px-1 py-0.5 transition-colors hover:bg-background-200/60 ${(item.stock ?? 0) === 0 ? 'font-medium text-accent-800' : 'text-foreground-800'}`}>
                          {item.stock ?? '—'}
                        </button>
                      )}
                      {item.oldStock != null && item.oldStock !== item.stock && <span className="tnum ml-1 text-[10px] text-foreground-400">({item.oldStock})</span>}
                    </td>
                    <td className="px-2 py-2">
                      {item.incoming > 0 ? (
                        <span className="tnum text-[11px] text-foreground-700">
                          +{item.incoming}
                          <span className="ml-1 text-foreground-400">{item.eta ? dayMonth(item.eta) : item.etaText}</span>
                        </span>
                      ) : (
                        <span className="text-[11px] text-foreground-300">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {editing === `${item.id}-price` ? (
                        <input autoFocus type="number" step="0.01" defaultValue={item.price ?? 0} onBlur={(e) => void patch(item, { price: Number(e.target.value) })} onKeyDown={(e) => { if (e.key === 'Enter') void patch(item, { price: Number((e.target as HTMLInputElement).value) }); }} className="tnum h-6 w-20 rounded border border-primary-400 bg-background-50 px-1 text-right text-[11.5px] outline-none" />
                      ) : (
                        <button type="button" onClick={() => setEditing(`${item.id}-price`)} className={`tnum cursor-pointer rounded px-1 py-0.5 font-medium transition-colors hover:bg-background-200/60 ${item.price == null ? 'text-rose-700' : 'text-foreground-950'}`}>
                          {item.price == null ? t('impX.setPrice') : aed(item.price)}
                        </button>
                      )}
                      {item.oldPrice != null && item.price != null && item.oldPrice !== item.price && (
                        <span className={`tnum ml-1 text-[10px] ${item.price > item.oldPrice ? 'text-rose-600' : 'text-primary-700'}`}>{item.price > item.oldPrice ? '▲' : '▼'} {aed(item.oldPrice)}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {editing === `${item.id}-old` ? (
                        <input autoFocus type="number" step="0.01" defaultValue={item.oldStockPrice ?? 0} onBlur={(e) => void patch(item, { oldStockPrice: Number(e.target.value) || null })} onKeyDown={(e) => { if (e.key === 'Enter') void patch(item, { oldStockPrice: Number((e.target as HTMLInputElement).value) || null }); }} className="tnum h-6 w-20 rounded border border-primary-400 bg-background-50 px-1 text-right text-[11.5px] outline-none" />
                      ) : (
                        <button type="button" onClick={() => setEditing(`${item.id}-old`)} className={`tnum cursor-pointer rounded px-1 py-0.5 text-[11px] hover:bg-background-200/60 ${item.oldStockPrice == null ? 'text-foreground-300' : 'text-foreground-600'}`}>
                          {item.oldStockPrice == null ? '—' : aed(item.oldStockPrice)}
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex max-w-[190px] flex-wrap gap-1">
                        {item.options.map((o) => (
                          <span key={o.label} className="rounded border border-background-200 bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-600">
                            {o.label}
                            {o.surcharge > 0 && <span className="tnum ml-0.5 font-medium text-foreground-900">+{o.surcharge}</span>}
                          </span>
                        ))}
                        {item.packingVariants.map((v) => (
                          <span key={v.packing} className="tnum rounded border border-background-200 bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-600">
                            {v.packing} {v.price}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      {editing === `${item.id}-group` ? (
                        <input autoFocus defaultValue={item.stockGroup ?? ''} onBlur={(e) => void patch(item, { stockGroup: e.target.value || null })} onKeyDown={(e) => { if (e.key === 'Enter') void patch(item, { stockGroup: (e.target as HTMLInputElement).value || null }); }} className="h-6 w-32 rounded border border-primary-400 bg-background-50 px-1 text-[10.5px] outline-none" />
                      ) : (
                        <button type="button" onClick={() => setEditing(`${item.id}-group`)} className={`tnum cursor-pointer rounded px-1.5 py-0.5 text-[10.5px] font-medium hover:bg-background-200/60 ${item.stockGroup ? 'bg-secondary-50 text-secondary-800' : 'text-foreground-300'}`}>
                          {item.stockGroup ?? '—'}
                        </button>
                      )}
                    </td>
                    <td className={`tnum px-2 py-2 text-right ${item.riskAed >= 2000 ? 'font-semibold text-accent-800' : 'text-foreground-600'}`}>{Math.round(item.riskAed).toLocaleString('en-AE')}</td>
                    <td className="px-2 py-2">
                      <div className="flex max-w-[200px] flex-wrap gap-1">
                        {item.flags.map((f) => (
                          <span key={f} className={`rounded border px-1 py-0.5 text-[9.5px] font-medium ${f === 'no_cbm' || f === 'new_item' ? 'border-background-200 bg-background-100 text-foreground-600' : 'border-rose-200 bg-rose-50 text-rose-700'}`} title={flagLabel(f)}>
                            {flagLabel(f)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-medium ${confidenceStyle[item.confidence]}`}>
                        <i className={`${item.confidence === 'high' ? 'ri-checkbox-circle-line' : item.confidence === 'check' ? 'ri-question-line' : 'ri-error-warning-line'} text-[10px]`} />
                        {confidenceLabel[item.confidence]} <span className="tnum text-foreground-400">{Math.round(item.confidenceScore * 100)}</span>
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
                        <input type="checkbox" checked={item.reviewed} onChange={() => void patch(item, { reviewed: !item.reviewed })} className="h-3.5 w-3.5 cursor-pointer accent-[oklch(var(--primary-500))]" />
                        <span className={needs ? 'font-semibold text-accent-800' : 'text-foreground-500'}>{item.reviewRequired ? t('impX.required') : t('impX.optional')}</span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2 border-t border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] text-foreground-500">{t('impReview.footer', { shown: rows.length, total: all.length, pages: version.filePages ?? 0 })}</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onBack} className="h-7 cursor-pointer whitespace-nowrap rounded border border-background-300 px-2.5 text-[11px] font-medium text-foreground-700 hover:bg-background-100">
              {t('impReview.back')}
            </button>
            <button type="button" onClick={onNext} className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded bg-primary-500 px-3 text-[11px] font-semibold text-background-50 hover:bg-primary-600">
              {t('impX.compare')} <i className="ri-arrow-right-line text-[11px]" />
            </button>
          </div>
        </div>
      </div>
      {openId && all.find((i) => i.id === openId) && (
        <ItemDrawer item={all.find((i) => i.id === openId) as ImportItem} versionId={version.id} onClose={() => setOpenId(null)} onPatch={(body) => void patch(all.find((i) => i.id === openId) as ImportItem, body)} />
      )}
    </div>
  );
}
